import { useMemo } from 'react';
import dayjs from 'dayjs';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import { TEST_RUN_EXECUTION_HISTORY } from '@elementary/common-ui/constants/queryKeys';
import { TestInvocation } from '@elementary/common-ui/types';
import { getTestRunExecutionHistoryQuery } from '../api';
import { TestRunExecutionHistoryResponse } from '../types';

type Props = {
  subTestUniqueId: string;
  enabled?: boolean;
};

const useTestRunExecutionHistory = ({
  subTestUniqueId,
  enabled = true,
}: Props) => {
  const envid = useSelectedEnv();

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: dayjs()
        .subtract(DEFAULT_DAYS_BACK, 'day')
        .startOf('d')
        .format(),
      endTime: dayjs().endOf('d').format(),
    }),
    [],
  );

  const { data, isFetching } = useGraphqlQuery<TestRunExecutionHistoryResponse>(
    {
      queryKey: [TEST_RUN_EXECUTION_HISTORY, envid, subTestUniqueId, startTime],
      query: getTestRunExecutionHistoryQuery,
      variables: { id: subTestUniqueId, startTime, endTime },
      enabled: enabled && !!subTestUniqueId,
      envId: envid,
    },
  );

  const invocations = useMemo<TestInvocation[]>(
    () =>
      (data?.latestTestExecution?.test?.executionHistory || []).map((item) => ({
        id: item.id,
        time_utc: item.executionTimestamp,
        status: item.status as TestInvocation['status'],
        affected_rows:
          item.failures != null ? Number(item.failures) : undefined,
      })),
    [data],
  );

  return { invocations, isFetching };
};

export default useTestRunExecutionHistory;
